// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/SpendAnalytics.tsx
//
//  Fase 3 · Iter 3.2 — Spend Analytics (gastos por fornecedor/produto/categoria).
//  Inclui tendência mensal, curva de Pareto (80/20) e drill-down por tabela.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  BarChart3, Building2, DollarSign, Package, PieChart as PieChartIcon,
  Receipt, ShoppingCart, TrendingUp,
} from 'lucide-react';
import { format, subMonths } from 'date-fns';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { useSpendAnalytics } from '@/features/compras/hooks/useRecebimento';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FornecedorCombobox } from '@/components/FornecedorCombobox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNumber = (v: number) => v.toLocaleString('pt-BR');

const CORES = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

const SpendAnalytics = () => {
  const hoje = new Date();
  const [dataInicio, setDataInicio] = useState(format(subMonths(hoje, 12), 'yyyy-MM-dd'));
  const [dataFim,    setDataFim]    = useState(format(hoje, 'yyyy-MM-dd'));
  // USA-COM-15: o backend já filtra por fornecedor.
  const [fornecedorId, setFornecedorId] = useState<number | null>(null);

  const { data, isLoading } = useSpendAnalytics({
    dataInicio, dataFim, fornecedorId: fornecedorId ?? undefined,
  });

  const kpis    = data?.resumo;
  const serie   = data?.seriePorMes ?? [];
  const forn    = data?.topFornecedoresPareto ?? [];
  const prod    = data?.topProdutosPareto ?? [];
  const grupos  = data?.gastoPorGrupo ?? [];

  const chartSerie = serie.map((m) => ({
    mes:    m.rotuloMes,
    valor:  m.valorGasto,
  }));

  const paretoFornData = forn.map((f) => ({
    nome:       f.fornecedorNome ?? '(sem nome)',
    valor:      f.valorGasto,
    acumulado:  f.percentualAcumulado,
  }));

  const paretoProdData = prod.map((p) => ({
    nome:       (p.descricao ?? '(sem nome)').slice(0, 24),
    valor:      p.valorGasto,
    acumulado:  p.percentualAcumulado,
  }));

  const gruposTop = grupos.slice(0, 8).map((g, i) => ({
    nome:   g.grupoNome ?? '(grupo sem nome)',
    valor:  g.valorGasto,
    fill:   CORES[i % CORES.length],
  }));

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Spend Analytics · Compras</h1>
        <p className="text-muted-foreground mt-1">
          Análise de gastos a partir dos recebimentos confirmados.
          Curva de Pareto destaca os fornecedores/produtos que concentram 80% dos gastos (curva A).
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label>Data inicial</Label>
            <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Data final</Label>
            <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>
          <div className="space-y-1 w-72">
            <Label>Fornecedor</Label>
            <FornecedorCombobox value={fornecedorId} onChange={setFornecedorId}
              placeholder="Todos os fornecedores" />
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {isLoading || !kpis ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <KpiCard
            icon={<DollarSign className="h-4 w-4 text-blue-500" />}
            label="Total gasto"
            valor={fmt(kpis.totalGasto)}
            subtitulo={`Médio/mês ${fmt(kpis.gastoMedioMensal)}`}
          />
          <KpiCard
            icon={<Receipt className="h-4 w-4 text-green-500" />}
            label="Recebimentos"
            valor={fmtNumber(kpis.totalRecebimentos)}
            subtitulo={`Ticket médio ${fmt(kpis.ticketMedioRecebimento)}`}
          />
          <KpiCard
            icon={<Building2 className="h-4 w-4 text-amber-500" />}
            label="Fornecedores"
            valor={fmtNumber(kpis.qtdFornecedoresUnicos)}
          />
          <KpiCard
            icon={<Package className="h-4 w-4 text-purple-500" />}
            label="Produtos"
            valor={fmtNumber(kpis.qtdProdutosUnicos)}
          />
          <KpiCard
            icon={<ShoppingCart className="h-4 w-4 text-cyan-500" />}
            label="Itens"
            valor={fmtNumber(kpis.totalItens)}
          />
        </div>
      )}

      {/* Tendência mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Tendência mensal
          </CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : chartSerie.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem dados no período.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSerie}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => fmt(v)} labelFormatter={(l) => `Mês: ${l}`} />
                <Line type="monotone" dataKey="valor" name="Gasto" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Pareto de fornecedores + pizza de grupos em grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Pareto por fornecedor
            </CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : paretoFornData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Sem dados.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoFornData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                  <YAxis yAxisId="left"  tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    formatter={(v: number, n: string) =>
                      n === 'acumulado' ? `${v.toFixed(1)}%` : fmt(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="valor" name="Gasto" fill="#3b82f6" />
                  <Line yAxisId="right" type="monotone" dataKey="acumulado" name="Acumulado %" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="h-4 w-4" /> Gasto por categoria
            </CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : gruposTop.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Sem dados.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={gruposTop}
                    dataKey="valor"
                    nameKey="nome"
                    cx="50%" cy="50%"
                    outerRadius={80}
                    label={(e) => `${e.nome}`}
                    labelLine={false}
                  >
                    {gruposTop.map((g, i) => (
                      <Cell key={i} fill={g.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabela de fornecedores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top fornecedores (detalhe)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : forn.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead className="text-right">Recebimentos</TableHead>
                    <TableHead className="text-right">Valor gasto</TableHead>
                    <TableHead className="text-right">% total</TableHead>
                    <TableHead className="text-right">% acum.</TableHead>
                    <TableHead>Curva</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forn.map((f) => (
                    <TableRow key={f.fornecedorId}>
                      <TableCell className="text-xs">{f.posicao}</TableCell>
                      <TableCell className="max-w-[280px] truncate" title={f.fornecedorNome ?? ''}>
                        {f.fornecedorNome ?? '(sem nome)'}
                      </TableCell>
                      <TableCell className="text-right text-xs">{f.qtdRecebimentos}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(f.valorGasto)}</TableCell>
                      <TableCell className="text-right text-xs">{f.percentualDoTotal.toFixed(2)}%</TableCell>
                      <TableCell className="text-right text-xs">{f.percentualAcumulado.toFixed(2)}%</TableCell>
                      <TableCell>
                        {f.isCorte80 ? (
                          <Badge className="bg-amber-500/10 text-amber-700 border-0" variant="secondary">A</Badge>
                        ) : f.percentualAcumulado <= 95 ? (
                          <Badge variant="outline">B</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">C</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela de produtos */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top produtos (detalhe)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : prod.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">Qtd total</TableHead>
                    <TableHead className="text-right">Valor gasto</TableHead>
                    <TableHead className="text-right">% total</TableHead>
                    <TableHead className="text-right">% acum.</TableHead>
                    <TableHead>Curva</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prod.map((p) => (
                    <TableRow key={p.produtoId}>
                      <TableCell className="text-xs">{p.posicao}</TableCell>
                      <TableCell className="max-w-[240px] truncate" title={p.descricao ?? ''}>
                        {p.descricao ?? '(sem nome)'}
                      </TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate" title={p.grupoNome ?? ''}>
                        {p.grupoNome ?? '—'}
                      </TableCell>
                      <TableCell className="text-right text-xs">{fmtNumber(p.quantidadeTotal)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(p.valorGasto)}</TableCell>
                      <TableCell className="text-right text-xs">{p.percentualDoTotal.toFixed(2)}%</TableCell>
                      <TableCell className="text-right text-xs">{p.percentualAcumulado.toFixed(2)}%</TableCell>
                      <TableCell>
                        {p.isCorte80 ? (
                          <Badge className="bg-amber-500/10 text-amber-700 border-0" variant="secondary">A</Badge>
                        ) : p.percentualAcumulado <= 95 ? (
                          <Badge variant="outline">B</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">C</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pareto de produtos (mini gráfico) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Pareto por produto
          </CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : paretoProdData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Sem dados.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={paretoProdData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="nome" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                <YAxis yAxisId="left"  tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  formatter={(v: number, n: string) =>
                    n === 'acumulado' ? `${v.toFixed(1)}%` : fmt(v)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="valor" name="Gasto" fill="#22c55e" />
                <Line yAxisId="right" type="monotone" dataKey="acumulado" name="Acumulado %" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SpendAnalytics;

// ── KpiCard ──────────────────────────────────────────────────────────────────

function KpiCard({
  icon, label, valor, subtitulo,
}: {
  icon:       React.ReactNode;
  label:      string;
  valor:      string;
  subtitulo?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-normal">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold">{valor}</div>
        {subtitulo && (
          <p className="text-xs text-muted-foreground mt-1">{subtitulo}</p>
        )}
      </CardContent>
    </Card>
  );
}
